import * as Icons from "@ant-design/icons";
import { Badge, Menu } from "antd";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import IntlMessages from "utils/IntlMessages";
import { hasAnyRole, isCurrentRole } from "./menuHelpers";

const { ItemGroup: MenuItemGroup, Item: MenuItem } = Menu;

const CountBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <Badge
      showZero={false}
      status="processing"
      offset={[3, -7]}
      size="small"
      count={count}
      style={{ fontSize: 11 }}
    />
  );
};

CountBadge.propTypes = { count: PropTypes.number };

const EspnMenuSection = ({
  espnAdminItems = [],
  espnFieldItems = [],
  counts = {},
  countsLoading = false,
  roles = [],
  role,
}) => {
  // An item shows if the user holds one of its roles across programs
  // AND their currently selected program role is one of them.
  const canSee = (item) => {
    if (!item.roles?.length) return true; // no restriction
    return (
      hasAnyRole(roles, ...item.roles) && isCurrentRole(role, ...item.roles)
    );
  };

  const fieldItems = espnFieldItems.filter(canSee);
  const adminItems = espnAdminItems.filter(canSee);

  const renderItem = (item) => {
    const Icon = item.icon && Icons[item.icon];
    const count = !countsLoading ? counts[item.base] : 0;
    return (
      <MenuItem key={item.key}>
        <Link to={`/espn/${item.base}`}>
          {Icon && <Icon className="icon" />}
          <span style={{ wordWrap: "break-word" }}>
            <IntlMessages id={item.id} />
            <CountBadge count={count} />
          </span>
        </Link>
      </MenuItem>
    );
  };

  return (
    <>
      <MenuItemGroup
        key="espn_analytics"
        className="gx-menu-group"
        title={<IntlMessages id="espn.analytics" />}
      >
        <MenuItem key="espn/dashboard">
          <Link to="/espn/dashboard">
            <Icons.PieChartOutlined className="icon" />
            <span>
              <IntlMessages id="espn.dashboard" />
            </span>
          </Link>
        </MenuItem>
      </MenuItemGroup>

      {fieldItems.length > 0 && (
        <MenuItemGroup
          key="espn_field"
          className="gx-menu-group"
          title={<IntlMessages id="espn.field" />}
        >
          {fieldItems.map(renderItem)}
        </MenuItemGroup>
      )}

      {adminItems.length > 0 && (
        <MenuItemGroup
          key="espn_administration"
          className="gx-menu-group"
          title={
            <span className="items-center">
              <IntlMessages id="espn.administration" />
            </span>
          }
        >
          {adminItems.map(renderItem)}
        </MenuItemGroup>
      )}
    </>
  );
};

EspnMenuSection.propTypes = {
  espnAdminItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      base: PropTypes.string.isRequired,
      icon: PropTypes.string,
      id: PropTypes.string.isRequired,
      roles: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  espnFieldItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      base: PropTypes.string.isRequired,
      icon: PropTypes.string,
      id: PropTypes.string.isRequired,
      roles: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  counts: PropTypes.object,
  countsLoading: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
  role: PropTypes.string,
};

export default EspnMenuSection;